import React, { useEffect, useState } from 'react'
import {
    ColumnDef,
    ColumnFiltersState,
    FilterFn,
    flexRender,
    getCoreRowModel,
    getFilteredRowModel,
    getPaginationRowModel,
    getSortedRowModel,
    useReactTable,
} from '@tanstack/react-table'
import { RankingInfo, rankItem } from '@tanstack/match-sorter-utils'
import { Table } from 'react-bootstrap'

declare module '@tanstack/react-table' {
    interface FilterFns {
        fuzzy: FilterFn<unknown>
    }
    interface FilterMeta {
        itemRank: RankingInfo
    }
}

const fuzzyFilter: FilterFn<any> = (row, columnId, value, addMeta) => {
    const itemRank = rankItem(row.getValue(columnId), value)
    addMeta({ itemRank })
    return itemRank.passed
}
interface DataTableServerSidePayoutSummaryTable {
    description: string;
    from: number;
    to: number;
    delta: number;
}

export const DataTableServerSidePayoutSummary = () => {
    const [data, setData] = useState<DataTableServerSidePayoutSummaryTable[]>([]);
    const [columnFilters, setColumnFilters] = useState<ColumnFiltersState>([])
    const [globalFilter, setGlobalFilter] = useState('')

    const columns = React.useMemo<ColumnDef<DataTableServerSidePayoutSummaryTable, any>[]>(() => [
        {
            accessorKey: 'description',
            id: 'description',
            header: 'Description',
            cell: (info) => info.getValue(),
            filterFn: 'includesString',
        },
        {
            accessorKey: 'from',
            id: 'from',
            header: 'From',
            cell: (info) => `$ ${info.getValue().toLocaleString()}`,
            filterFn: 'includesString',
        },
        {
            accessorKey: 'to',
            id: 'to',
            header: 'To',
            cell: (info) => `$ ${info.getValue().toLocaleString()}`,
            filterFn: 'includesString',
        },
        {
            accessorKey: 'delta',
            id: 'delta',
            header: 'Delta',
            cell: (info) => `$ ${info.getValue().toLocaleString()}`,
            filterFn: 'includesString',
        },
    ], []);

    useEffect(() => {
        const fetchData = async () => {
            try {
                const response = await fetch('https://dummyjson.com/c/f052-1c12-4541-897c')
                const result = await response.json()
                console.log(result, 'Dummy data from API')
                setData(result)
            } catch (error) {
                console.error('Error fetching data:', error)
            }
        }

        fetchData()
    }, [])

    const table = useReactTable({
        data,
        columns,
        filterFns: {
            fuzzy: fuzzyFilter,
        },
        state: {
            columnFilters,
            globalFilter,
        },
        onColumnFiltersChange: setColumnFilters,
        onGlobalFilterChange: setGlobalFilter,

        globalFilterFn: 'fuzzy',
        getCoreRowModel: getCoreRowModel(),
        getFilteredRowModel: getFilteredRowModel(),
        getSortedRowModel: getSortedRowModel(),
        getPaginationRowModel: getPaginationRowModel(),
    })

    return (
        <>
            <div className="bg-white rounded p-4" style={{ overflowX: 'auto' }}>
                <Table className="w-100" style={{ minWidth: '600px' }}>
                    <thead className='custom-thead border-bottom'>
                        {table.getHeaderGroups().map((headerGroup) => (
                            <tr
                                key={headerGroup.id}
                                className='font-size-12 font-weight-400 table-row border-dark-mode'
                            >
                                {headerGroup.headers.map((header) => (
                                    <th key={header.id} colSpan={header.colSpan} className='min-w-120px pb-6 pt-4'>
                                        {header.isPlaceholder ? null : (
                                            <div
                                                className={header.column.getCanSort() ? 'cursor-pointer select-none' : ''}
                                                onClick={header.column.getToggleSortingHandler()}
                                            >
                                                {flexRender(header.column.columnDef.header, header.getContext())}
                                                {{
                                                    asc: ' ▲',
                                                    desc: ' ▼',
                                                }[header.column.getIsSorted() as string] ?? null}
                                            </div>
                                        )}
                                    </th>
                                ))}
                            </tr>
                        ))}
                    </thead>
                    <tbody>
                        {table.getRowModel().rows.map((row) => (
                            <tr key={row.id} className="border-bottom">
                                {row.getVisibleCells().map((cell) => (
                                    <td key={cell.id} className="pt-5 border-0">
                                        {flexRender(cell.column.columnDef.cell, cell.getContext())}
                                    </td>
                                ))}
                            </tr>
                        ))}
                    </tbody>
                </Table>
            </div>
        </>
    );
};
